
/**
 * Normalization layer to convert multi-file upload, folder upload, and drag/drop upload
 * into the same system.
 */

import { Signal } from '../async/Signal';
import { IFileSystemEntry, IFileSystemFileEntry, IFileSystemDirectoryReader } from './IFileSystem';
import { IFile } from './IFile';

/**
 * @public
 */
export const enum EntryType {
  unknown,
  file,
  directory
}

/**
 * @public
 */
export interface IBaseEntry {
  name: string;
}

/**
 * @public
 */
export interface IFileEntry extends IBaseEntry {
  type: EntryType.file;

  getFile(): Promise<IFile>;
}

/**
 * @public
 */
export interface IUnknownEntry extends IBaseEntry {
  type: EntryType.unknown;

  getFile(): Promise<IFile>;
}

/**
 * @public
 */
export interface IDirectoryPage {
  entries: IEntry[];

  getEntries?: (() => Promise<IDirectoryPage>) | undefined;
}

/**
 * @public
 */
export interface IDirectoryEntry extends IBaseEntry {
  type: EntryType.directory;

  getEntries(): Promise<IDirectoryPage>;
}

/**
 * @public
 */
export type IEntry = IFileEntry | IDirectoryEntry | IUnknownEntry;

interface IFolderTree {
  name: string;
  directories: {
    [name: string]: IFolderTree;
  };
  files: IFile[];
}

function buildDirectoryEntry(tree: IFolderTree): IDirectoryEntry {
  return {
    name: tree.name,
    type: EntryType.directory,
    getEntries: () => {
      return Promise.resolve({
        entries: buildEntries(tree)
      });
    }
  };
}

function buildFileEntry(file: IFile): IFileEntry | IUnknownEntry {
  let entry: IFileEntry | IUnknownEntry;

  if (file.type) {
    entry = {
      name: file.name,
      type: EntryType.file,
      getFile: () => Promise.resolve(file)
    };
  } else {
    entry = {
      name: file.name,
      type: EntryType.unknown,
      getFile: () => Promise.resolve(file)
    };
  }

  return entry;
}

function buildEntries(tree: IFolderTree): IEntry[] {
  const entries: IEntry[] = [];

  for (const name in tree.directories) {
    if (name) {
      entries.push(buildDirectoryEntry(tree.directories[name]));
    }
  }

  for (const file of tree.files) {
    entries.push(buildFileEntry(file));
  }

  return entries;
}

/**
 * @public
 */
export function buildEntriesFromFileList(files: ReadonlyArray<IFile>): IEntry[] {
  const root: IFolderTree = {
    name: '',
    directories: {},
    files: []
  };

  for (const file of files) {
    const {
      webkitRelativePath: path = file.name
    } = file;

    let folder = root;

    for (const part of path.split('/').slice(0, -1)) {
      folder = folder.directories[part] || (folder.directories[part] = {
        name: part,
        directories: {},
        files: []
      });
    }

    folder.files.push(file);
  }

  return buildEntries(root);
}

function readEntries(reader: IFileSystemDirectoryReader): Promise<IFileSystemEntry[]> {
  const {
    resolve,
    reject,
    promise
  } = new Signal<IFileSystemEntry[]>();

  reader.readEntries(resolve, reject);

  return promise;
}

function getFile(fileSystemFileEntry: IFileSystemFileEntry): Promise<IFile> {
  const {
    resolve,
    reject,
    promise
  } = new Signal<IFile>();

  fileSystemFileEntry.file(resolve, reject);

  return promise;
}

async function getPage(reader: IFileSystemDirectoryReader): Promise<IDirectoryPage> {
  const fileSystemEntries = await readEntries(reader);

  let page: IDirectoryPage;

  if (fileSystemEntries.length) {
    page = {
      entries: fileSystemEntries.map(getEntry),
      getEntries: () => getPage(reader)
    };
  } else {
    page = {
      entries: []
    };
  }

  return page;
}

function getEntry(fileSystemEntry: IFileSystemEntry): IEntry {
  let entry: IEntry;

  if (fileSystemEntry.isDirectory === true) {
    entry = {
      name: fileSystemEntry.name,
      type: EntryType.directory,
      getEntries: () => {
        return getPage(fileSystemEntry.createReader());
      }
    };
  } else {
    entry = {
      name: fileSystemEntry.name,
      type: EntryType.file,
      getFile: () => {
        return getFile(fileSystemEntry);
      }
    };
  }

  return entry;
}

/**
 * @public
 */
export function buildEntriesFromEntryList(fileSystemEntries: ReadonlyArray<IFileSystemEntry>): IEntry[] {
  return fileSystemEntries.map(getEntry);
}
